import { Alert, Badge, Button, EmptyState, Field, Input } from '@personachat/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileDown, Filter, KeyRound, Plus, Power, PowerOff, Trash2 } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components, paths } from '../../api/schema';
import { DataTable, type Colonne } from '../../components/DataTable';
import { Code, ErreurChargement, Filtre, Jauge, Recherche, Select } from '../../components/bits';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { csv, enregistrer, nomDate } from '../../lib/fichiers';
import { bornesJourParis, formatDate, formatNombre, jourParis } from '../../lib/format';
import { EFFET_TYPE, LIBELLE_TYPE, statutCle, TON_TYPE, TYPES_COMPTE, type TypeCompte } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';
import { useEvenements, type TypeEvenement } from '../events/EventsPage';

type Cle = components['schemas']['AccessKeyListItem'];
type RequeteCles = NonNullable<paths['/api/admin/access-keys']['get']['parameters']['query']>;

const FILTRES = { search: '', keyType: 'all', active: 'all', usage: 'all', expired: 'all', evenement: '', tri: 'createdAt', ordre: 'desc', page: '1' };

function requeteCles(f: typeof FILTRES): RequeteCles {
  return {
    page: Number(f.page) || 1,
    limit: 25,
    sort: f.tri as RequeteCles['sort'],
    order: f.ordre as 'asc' | 'desc',
    ...(f.search.trim() ? { search: f.search.trim() } : {}),
    ...(f.keyType !== 'all' ? { keyType: f.keyType as RequeteCles['keyType'] } : {}),
    ...(f.active !== 'all' ? { active: f.active as 'true' | 'false' } : {}),
    ...(f.usage !== 'all' ? { usage: f.usage as RequeteCles['usage'] } : {}),
    ...(f.expired !== 'all' ? { expired: f.expired as 'true' | 'false' } : {}),
    ...(f.evenement ? { eventId: f.evenement } : {}),
  };
}

/**
 * Clés d'accès (cahier des charges §7.4) : le type de compte créé par une clé
 * est visible et choisi à la création ; fiche d'une clé en panneau (/cles-acces/:id).
 */
export function AccessKeysPage() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: ouverte } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<Map<string, Cle>>(new Map());
  const [action, setAction] = useState<'activer' | 'desactiver' | 'supprimer' | null>(null);
  const [export_, setExport] = useState(false);
  const creer = params.get('creer') === '1';
  const evenements = useEvenements();

  const liste = useQuery({
    queryKey: ['cles-acces', f],
    queryFn: () => lire(api.GET('/api/admin/access-keys', { params: { query: requeteCles(f) } })),
    placeholderData: keepPreviousData,
  });

  useEffect(() => setSelection(new Map()), [JSON.stringify({ ...f, page: undefined })]);

  const ouvrirCreation = (o: boolean) =>
    setParams(
      (a) => {
        const s = new URLSearchParams(a);
        if (o) s.set('creer', '1');
        else s.delete('creer');
        return s;
      },
      { replace: true }
    );

  const colonnes: Colonne<Cle>[] = [
    {
      id: 'code',
      header: 'Code',
      cell: (k) => (
        <div>
          <Code valeur={k.key} />
          {k.description && <div className="cell-sub">{k.description}</div>}
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (k) => (
        <div>
          <Badge tone={TON_TYPE[k.keyType]}>{LIBELLE_TYPE[k.keyType]}</Badge>
          {k.event && <div className="cell-sub">{k.event.name}</div>}
        </div>
      ),
    },
    { id: 'usage', header: 'Utilisations', sortKey: 'usedCount', cell: (k) => <Jauge utilise={k.usedCount} max={k.maxUses} /> },
    { id: 'expiration', header: 'Expiration', sortKey: 'expiresAt', cell: (k) => (k.expiresAt ? formatDate(k.expiresAt) : <span className="muted">Aucune</span>) },
    {
      id: 'statut',
      header: 'Statut',
      cell: (k) => {
        const s = statutCle(k);
        return <Badge tone={s.ton}>{s.libelle}</Badge>;
      },
    },
    { id: 'creation', header: 'Création', sortKey: 'createdAt', secondaire: true, cell: (k) => formatDate(k.createdAt) },
  ];

  const selectionnees = [...selection.values()];
  const supprimables = selectionnees.filter((k) => k.usedCount === 0);
  const ids = selectionnees.map((k) => k.id);

  const actionGroupee = async () => {
    if (action === 'supprimer') {
      await lire(api.DELETE('/api/admin/access-keys/bulk-delete', { body: { keyIds: supprimables.map((k) => k.id) } }));
    } else if (action === 'activer') {
      await lire(api.PATCH('/api/admin/access-keys/bulk-activate', { body: { keyIds: ids } }));
    } else if (action === 'desactiver') {
      await lire(api.PATCH('/api/admin/access-keys/bulk-deactivate', { body: { keyIds: ids } }));
    }
    await queryClient.invalidateQueries({ queryKey: ['cles-acces'] });
    toast.succes(action === 'supprimer' ? `${supprimables.length} clé(s) supprimée(s).` : action === 'activer' ? 'Clés activées.' : 'Clés désactivées.');
    setSelection(new Map());
  };

  const exporter = async () => {
    setExport(true);
    try {
      const { page: _p, limit: _l, sort: _s, order: _o, ...query } = requeteCles(f);
      enregistrer(await lire(api.GET('/api/admin/access-keys/export', { params: { query }, parseAs: 'blob' })), nomDate('cles-acces'));
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setExport(false);
    }
  };

  const exporterSelection = () =>
    enregistrer(
      csv([
        ['code', 'type', 'description', 'utilisations', 'maximum', 'expiration', 'statut'],
        ...selectionnees.map((k) => [k.key, LIBELLE_TYPE[k.keyType], k.description, k.usedCount, k.maxUses, k.expiresAt, statutCle(k).libelle]),
      ]),
      nomDate('cles-selection')
    );

  const total = liste.data?.pagination.totalCount;

  return (
    <>
      <PageHeader
        title="Clés d’accès"
        lead={total !== undefined ? `${formatNombre(total)} clé${total > 1 ? 's' : ''}. Une clé décide du type de compte créé à l’inscription.` : 'Une clé décide du type de compte créé à l’inscription.'}
        actions={
          <>
            <Button icon={<Download size={15} aria-hidden="true" />} onClick={() => void exporter()} loading={export_}>
              Exporter
            </Button>
            <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => ouvrirCreation(true)}>
              Créer des clés
            </Button>
          </>
        }
      />

      <div className="toolbar" role="search">
        <Recherche id="cles-recherche" label="Rechercher une clé" valeur={f.search} onChange={(search) => modifier({ search })} placeholder="Code ou cohorte" />
        <Filtre id="c-type" label="Type">
          <Select id="c-type" value={f.keyType} onChange={(e) => modifier({ keyType: e.target.value })}>
            <option value="all">Tous</option>
            {TYPES_COMPTE.map((t) => (
              <option key={t} value={t}>
                {LIBELLE_TYPE[t]}
              </option>
            ))}
          </Select>
        </Filtre>
        <Filtre id="c-statut" label="Statut">
          <Select id="c-statut" value={f.active} onChange={(e) => modifier({ active: e.target.value })}>
            <option value="all">Tous</option>
            <option value="true">Active</option>
            <option value="false">Désactivée</option>
          </Select>
        </Filtre>
        <Filtre id="c-usage" label="Utilisation">
          <Select id="c-usage" value={f.usage} onChange={(e) => modifier({ usage: e.target.value })}>
            <option value="all">Toutes</option>
            <option value="unused">Jamais utilisée</option>
            <option value="used">Déjà utilisée</option>
            <option value="full">Pleine</option>
          </Select>
        </Filtre>
        <Filtre id="c-expiree" label="Expiration">
          <Select id="c-expiree" value={f.expired} onChange={(e) => modifier({ expired: e.target.value })}>
            <option value="all">Toutes</option>
            <option value="false">Valide</option>
            <option value="true">Expirée</option>
          </Select>
        </Filtre>
        <Filtre id="c-evenement" label="Événement">
          <Select id="c-evenement" value={f.evenement} onChange={(e) => modifier({ evenement: e.target.value })}>
            <option value="">Tous</option>
            {(evenements.data ?? []).map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </Select>
        </Filtre>
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer les filtres ({actifs})
          </Button>
        )}
      </div>

      {liste.isError ? (
        <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
      ) : (
        <DataTable
          libelle="Clés d’accès"
          colonnes={colonnes}
          lignes={liste.data?.accessKeys}
          cleLigne={(k) => k.id}
          chargement={liste.isFetching && !liste.data}
          tri={{ cle: f.tri, ordre: f.ordre as 'asc' | 'desc' }}
          onTri={(t) => modifier({ tri: t.cle, ordre: t.ordre })}
          selection={new Set(selection.keys())}
          onSelection={(s) => {
            const suivante = new Map<string, Cle>();
            for (const id of s) {
              const k = selection.get(id) ?? liste.data?.accessKeys.find((x) => x.id === id);
              if (k) suivante.set(id, k);
            }
            setSelection(suivante);
          }}
          onOuvrir={(k) => navigate(`/cles-acces/${k.id}${location.search}`)}
          estActive={(k) => k.id === ouverte}
          pagination={liste.data ? { page: liste.data.pagination.page, pages: liste.data.pagination.totalPages, total: liste.data.pagination.totalCount } : undefined}
          onPage={(page) => modifier({ page: String(page) })}
          vide={
            <EmptyState
              icon={<KeyRound size={28} aria-hidden="true" />}
              title={actifs || f.search ? 'Aucune clé ne correspond' : 'Aucune clé d’accès'}
              action={
                actifs ? (
                  <Button onClick={reinitialiser}>Effacer les filtres</Button>
                ) : (
                  <Button variant="primary" onClick={() => ouvrirCreation(true)}>
                    Créer des clés
                  </Button>
                )
              }
            />
          }
        />
      )}

      {selection.size > 0 && (
        <div className="selection-bar" role="region" aria-label="Actions sur la sélection">
          <span className="selection-bar__count">
            {formatNombre(selection.size)} clé{selection.size > 1 ? 's' : ''} sélectionnée{selection.size > 1 ? 's' : ''}
          </span>
          <Button size="sm" icon={<Power size={14} aria-hidden="true" />} onClick={() => setAction('activer')}>
            Activer
          </Button>
          <Button size="sm" icon={<PowerOff size={14} aria-hidden="true" />} onClick={() => setAction('desactiver')}>
            Désactiver
          </Button>
          <Button size="sm" icon={<Trash2 size={14} aria-hidden="true" />} onClick={() => setAction('supprimer')} disabled={supprimables.length === 0}>
            Supprimer
          </Button>
          <Button size="sm" icon={<FileDown size={14} aria-hidden="true" />} onClick={exporterSelection}>
            Exporter
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelection(new Map())}>
            Vider
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={!!action}
        onOpenChange={(o) => !o && setAction(null)}
        title={action === 'supprimer' ? 'Supprimer des clés' : action === 'activer' ? 'Activer des clés' : 'Désactiver des clés'}
        confirmLabel={action === 'supprimer' ? `Supprimer ${supprimables.length} clé(s)` : 'Confirmer'}
        tone={action === 'activer' ? 'primary' : 'danger'}
        onConfirm={actionGroupee}
      >
        {action === 'supprimer' ? (
          <>
            <p>
              <strong>{supprimables.length}</strong> clé(s) jamais utilisée(s) supprimée(s) définitivement.
            </p>
            {selectionnees.length > supprimables.length && (
              <p className="note">{selectionnees.length - supprimables.length} clé(s) déjà utilisée(s) conservée(s) : désactivez-les plutôt.</p>
            )}
          </>
        ) : action === 'activer' ? (
          <p>
            <strong>{selectionnees.length}</strong> clé(s) de nouveau utilisables à l’inscription (dans la limite de leurs utilisations et de leur
            expiration).
          </p>
        ) : (
          <p>
            <strong>{selectionnees.length}</strong> clé(s) refusées à l’inscription. Les comptes déjà créés ne sont pas touchés.
          </p>
        )}
      </ConfirmDialog>

      <CreateKeysDialog open={creer} onOpenChange={ouvrirCreation} evenementInitial={f.evenement} />
      <Outlet />
    </>
  );
}

/** Création unitaire ou par lot : type, cohorte, utilisations, expiration ; CSV à la fin d'un lot. */
function CreateKeysDialog({
  open,
  onOpenChange,
  evenementInitial,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Événement choisi d'avance (lien « Créer des clés pour cet événement »). */
  evenementInitial: string;
}) {
  const id = useId();
  const evenements = useEvenements();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'une' | 'lot'>('une');
  const [type, setType] = useState<TypeCompte | ''>('');
  const [description, setDescription] = useState('');
  const [utilisations, setUtilisations] = useState('1');
  const [nombre, setNombre] = useState('20');
  const [expiration, setExpiration] = useState('');
  const [evenement, setEvenement] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [creees, setCreees] = useState<Array<{ key: string; keyType: TypeCompte; description?: string | null; maxUses: number; expiresAt?: string | null }> | null>(null);

  useEffect(() => {
    if (open) {
      setMode('une');
      setType('');
      setDescription('');
      setUtilisations('1');
      setNombre('20');
      setExpiration('');
      setErreur(null);
      setCreees(null);
      const choisi = evenements.data?.find((e) => e.id === evenementInitial);
      setType(choisi ? choisi.userType : '');
      setEvenement(choisi ? choisi.id : '');
    }
  }, [open]);

  // Lien depuis une fiche événement ouverte directement : la liste des événements arrive après l'ouverture.
  useEffect(() => {
    if (!open || !evenementInitial || type !== '') return;
    const choisi = evenements.data?.find((e) => e.id === evenementInitial);
    if (choisi) {
      setType(choisi.userType);
      setEvenement(choisi.id);
    }
  }, [open, evenementInitial, evenements.data]);

  // Événements du type choisi, du plus récent au plus ancien : le premier est celui par défaut.
  const evenementsDuType = type === 'GUEST' || type === 'CHALLENGE3' ? (evenements.data ?? []).filter((e) => e.userType === (type as TypeEvenement)) : [];
  const choisirType = (t: TypeCompte) => {
    setType(t);
    setEvenement((actuel) => (evenements.data?.find((e) => e.id === actuel)?.userType === t ? actuel : ''));
  };

  const maxUses = Number(utilisations);
  const count = Number(nombre);
  const valide =
    type !== '' && Number.isInteger(maxUses) && maxUses >= 1 && (mode === 'une' || (Number.isInteger(count) && count >= 1 && count <= 1000));

  const creer = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide) return;
    setEnvoi(true);
    setErreur(null);
    const corps = {
      keyType: type,
      maxUses,
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(expiration ? { expiresAt: bornesJourParis(expiration, 'fin') } : {}),
      ...(evenement && type !== 'NORMAL' ? { eventId: evenement } : {}),
    };
    try {
      if (mode === 'une') {
        const r = await lire(api.POST('/api/admin/access-keys', { body: corps }));
        setCreees([r.accessKey]);
      } else {
        const r = await lire(api.POST('/api/admin/access-keys/batch', { body: { ...corps, count } }));
        setCreees(r.keys);
      }
      await queryClient.invalidateQueries({ queryKey: ['cles-acces'] });
      toast.succes(mode === 'une' ? 'Clé créée.' : `${count} clés créées.`);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  const telecharger = () =>
    creees &&
    enregistrer(
      csv([
        ['code', 'type', 'cohorte', 'utilisations', 'expiration'],
        ...creees.map((k) => [k.key, LIBELLE_TYPE[k.keyType], k.description, k.maxUses, k.expiresAt ? formatDate(k.expiresAt) : '']),
      ]),
      nomDate(`cles-${(description.trim() || 'lot').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`)
    );

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title="Créer des clés d’accès" width={600}>
      {creees ? (
        <div className="stack">
          <Alert tone="info">
            {creees.length === 1 ? 'Clé créée' : `${formatNombre(creees.length)} clés créées`} : chaque inscription avec
            {creees.length === 1 ? ' elle' : ' l’une d’elles'} crée un compte <strong>{LIBELLE_TYPE[creees[0]!.keyType]}</strong>.
          </Alert>
          {creees.length === 1 ? (
            <div>
              <Code valeur={creees[0]!.key} />
            </div>
          ) : (
            <ul className="plain-list" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {creees.slice(0, 50).map((k) => (
                <li key={k.key}>
                  <code>{k.key}</code>
                </li>
              ))}
              {creees.length > 50 && <li className="muted">… et {formatNombre(creees.length - 50)} autres dans le CSV</li>}
            </ul>
          )}
          <div className="modal__footer modal__footer--inline">
            <Button onClick={() => onOpenChange(false)}>Fermer</Button>
            <Button variant="primary" icon={<Download size={15} aria-hidden="true" />} onClick={telecharger}>
              Télécharger le CSV
            </Button>
          </div>
        </div>
      ) : (
        <form className="stack" onSubmit={creer}>
          <div className="segmented" role="group" aria-label="Nombre de clés">
            <button type="button" aria-pressed={mode === 'une'} onClick={() => setMode('une')}>
              Une clé
            </button>
            <button type="button" aria-pressed={mode === 'lot'} onClick={() => setMode('lot')}>
              Un lot de clés
            </button>
          </div>
          <fieldset className="choix">
            <legend className="ui-field__label">Type de compte créé à l’inscription</legend>
            {TYPES_COMPTE.map((t) => (
              <label key={t} className={t === type ? 'choix__option choix__option--active' : 'choix__option'}>
                <input type="radio" name={`${id}-type`} checked={t === type} onChange={() => choisirType(t)} />
                <span>
                  <strong>{LIBELLE_TYPE[t]}</strong>
                  <span className="note">{EFFET_TYPE[t]}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {evenementsDuType.length > 0 && (
            <Field id={`${id}-evenement`} label="Événement" hint="Module, calendrier et fin suivis par les comptes créés avec ces clés.">
              <Select id={`${id}-evenement`} value={evenement} onChange={(e) => setEvenement(e.target.value)}>
                <option value="">Le plus récent ({evenementsDuType[0]!.name})</option>
                {evenementsDuType.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field id={`${id}-desc`} label="Cohorte (description)" hint="Retrouvée dans la recherche et sur la fiche des élèves inscrites.">
            <Input id={`${id}-desc`} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} placeholder="Ex. : Cohorte novembre 2026" />
          </Field>
          <div className="grid-2">
            {mode === 'lot' && (
              <Field id={`${id}-nombre`} label="Nombre de clés" hint="1 000 au plus.">
                <Input id={`${id}-nombre`} inputMode="numeric" value={nombre} onChange={(e) => setNombre(e.target.value.replace(/[^\d]/g, ''))} />
              </Field>
            )}
            <Field id={`${id}-usages`} label={mode === 'lot' ? 'Utilisations par clé' : 'Utilisations'} hint="1 : une clé par élève.">
              <Input id={`${id}-usages`} inputMode="numeric" value={utilisations} onChange={(e) => setUtilisations(e.target.value.replace(/[^\d]/g, ''))} />
            </Field>
            <Field id={`${id}-exp`} label="Expire le (facultatif)" hint="Valable jusqu’à 23:59, heure de Paris.">
              <Input id={`${id}-exp`} type="date" min={jourParis()} value={expiration} onChange={(e) => setExpiration(e.target.value)} />
            </Field>
          </div>
          {erreur && <Alert>{erreur}</Alert>}
          {type === '' && <p className="note">Choisissez le type de compte : il ne pourra plus être changé.</p>}
          <div className="modal__footer modal__footer--inline">
            <Button onClick={() => onOpenChange(false)} disabled={envoi}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" loading={envoi} disabled={!valide}>
              {mode === 'une' ? 'Créer la clé' : `Créer ${Number.isInteger(count) && count > 0 ? formatNombre(count) : ''} clés`}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
